/**
 * Overlay renderer for the shared dock snap visuals. Two highlight shapes:
 *
 * 1. Full-edge bands (overlayRect = null): the classic screen-edge snap zones
 *    reusing the sidepanel overlay styling (`side-panel-snap-overlay-*` in
 *    side-panel.css) plus toolbar-strip overrides (`toolbar-snap-overlay-*`
 *    in toolbar.css).
 * 2. Entry-relative inserts (overlayRect set): a fixed-position band
 *    highlighting the exact slot beside/between docked entries (sidepanels,
 *    the toolbar rail). Styled by `.dock-insert-overlay` in side-panel.css so
 *    every dockable surface shows identical drop feedback.
 */

import { CSSProperties } from 'react';
import { DockSide } from '../store/useDockLayoutStore';
import { DockEntryRect } from './dockSnapResolver';

export interface DockOverlaySpec {
    side: DockSide | null;
    /** Exact geometry for entry-relative hits; null for edge bands */
    overlayRect?: DockEntryRect | null;
}

export const dockOverlayClass = (side: DockSide | null): string =>
    side
        ? `side-panel-snap-overlay side-panel-snap-overlay-${side} toolbar-snap-overlay`
        : '';

/** React node for both overlay shapes; consumers render it while dragging */
export const dockOverlayNode = ({
    side,
    overlayRect,
}: DockOverlaySpec): React.ReactNode => {
    if (!side) return null;
    if (overlayRect) {
        const style: CSSProperties = {
            left: overlayRect.left,
            top: overlayRect.top,
            width: overlayRect.width,
            height: overlayRect.height,
        };
        return <div className="dock-insert-overlay" style={style} />;
    }
    return <div className={dockOverlayClass(side)} />;
};
